import React from "react";
import dayjs from "dayjs";
import {
  Box, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader,
  DrawerOverlay, Flex, Icon, Link, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdOpenInNew } from "react-icons/md";
import { eventTypeLabel } from "constants/eventTypeLabels";
import ContactCardStatusChip from "./ContactCardStatusChip";
import { KV_ROW } from "./contactCardsManageStyles";
import { buildMapsUrl, formatCoordinates, hasCoordinates } from "tools/geo";

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDateTime(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Renders a labeled key/value row used in every detail section.
 * @param {Object} props - label (string), value (node)
 * @returns {React.ReactElement}
 */
function KvRow({ label, value }) {
  const keyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const valColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  return (
    <Flex {...KV_ROW}>
      <Text fontSize="xs" color={keyColor}>{label}</Text>
      <Text fontSize="sm" fontWeight="600" color={valColor} textAlign="right" wordBreak="break-word">{value || "—"}</Text>
    </Flex>
  );
}

/**
 * Renders an uppercase section title above a group of key/value rows.
 * @param {Object} props - children (label node)
 * @returns {React.ReactElement}
 */
function SectionTitle({ children }) {
  const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Text fontSize="xs" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase" color={color} mt="16px" mb="6px">
      {children}
    </Text>
  );
}

/**
 * Drawer header showing the status chips and the contact / reservation IDs.
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function HeaderBlock({ card }) {
  const idColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Box>
      <ContactCardStatusChip card={card} />
      <Text fontSize="xs" fontFamily="mono" color={idColor} mt="8px">
        ID : #{card?.id || "—"} · Réservation : #{card?.reservation_id || "—"}
      </Text>
    </Box>
  );
}

/**
 * Client section: contact party identity captured at contact creation.
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function ClientSection({ card }) {
  const r = card?.reservation || {};
  return (
    <Box>
      <SectionTitle>Client</SectionTitle>
      <KvRow label="Nom complet" value={[card?.name, card?.lastname].filter(Boolean).join(" ")} />
      <KvRow label="Email" value={r.email} />
      <KvRow label="Téléphone" value={r.phone} />
      <KvRow label="Adresse" value={[r.address, r.city, r.postal_code, r.country].filter(Boolean).join(", ")} />
      {hasCoordinates(r) && (
        <KvRow
          label="Coordonnées GPS"
          value={(
            <Link href={buildMapsUrl(r.latitude, r.longitude)} isExternal display="inline-flex" alignItems="center" gap="4px" color="brand.500">
              {formatCoordinates(r.latitude, r.longitude)}
              <Icon as={MdOpenInNew} w="14px" h="14px" />
            </Link>
          )}
        />
      )}
      <KvRow label="ID auditor" value={r.auditor_who_reserve_id ? `#${r.auditor_who_reserve_id}` : null} />
    </Box>
  );
}

/**
 * Reservation section: event metadata and the linked artist/group.
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function ReservationSection({ card }) {
  const descColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
  const descBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const r = card?.reservation || {};
  const artistName = card?.artist_profile?.artist_name || r.artist_name;
  const currency = (card?.payment?.currency || "").toUpperCase();
  const total = (Number(r.total_amount) || 0) + (Number(r.travel_expenses) || 0) + (Number(r.contact_fee) || 0);
  return (
    <Box>
      <SectionTitle>Réservation</SectionTitle>
      <KvRow label="Titre" value={r.title} />
      <KvRow label="Événement" value={eventTypeLabel(r.event)} />
      <KvRow label="Date événement" value={formatDateTime(r.event_date)} />
      <KvRow label="Artiste" value={artistName} />
      <KvRow label="Service ID" value={r.services_id ? `#${r.services_id}` : null} />
      <KvRow label="Montant prestation" value={r.total_amount != null ? `${r.total_amount} ${currency}`.trim() : null} />
      <KvRow label="Frais déplacement" value={r.travel_expenses != null ? r.travel_expenses : null} />
      <KvRow label="Frais de contact" value={r.contact_fee != null ? `${r.contact_fee} ${currency}`.trim() : null} />
      <KvRow label="Total réservation" value={`${total} ${currency}`.trim()} />
      <KvRow label="Politique remboursement" value={r.refund_policy} />
      {r.description && (
        <Box mt="8px" p="10px" borderRadius="10px" bg={descBg}>
          <Text fontSize="sm" color={descColor} whiteSpace="pre-wrap">{r.description}</Text>
        </Box>
      )}
    </Box>
  );
}

/**
 * Payment section: details + Stripe invoice link when the contact has been paid.
 * Hidden entirely when no payment is attached to the contact.
 * @param {Object} props - card
 * @returns {React.ReactElement|null}
 */
function PaymentSection({ card }) {
  const p = card?.payment;
  if (!p) return null;
  const currency = (p.currency || "").toUpperCase();
  const artistShare = p.artist_amount ?? p.artist_group_amount;
  return (
    <Box>
      <SectionTitle>Paiement</SectionTitle>
      <KvRow label="Méthode" value={p.payment_method_type} />
      <KvRow label="Montant total" value={p.total_amount != null ? `${p.total_amount} ${currency}`.trim() : null} />
      <KvRow label="Part ISL" value={p.isl_amount != null ? `${p.isl_amount} ${currency}`.trim() : null} />
      <KvRow label="Part artiste" value={artistShare != null ? `${artistShare} ${currency}`.trim() : null} />
      <KvRow label="Statut" value={p.paid ? "Payé" : (p.refunded ? "Remboursé" : "En attente")} />
      <KvRow label="Référence" value={p.reference} />
      {p.invoice && (
        <Link href={p.invoice} isExternal display="inline-flex" alignItems="center" gap="4px" color="brand.500" fontSize="sm" mt="8px">
          <Icon as={MdOpenInNew} w="16px" h="16px" /> Voir le reçu Stripe
        </Link>
      )}
    </Box>
  );
}

/**
 * Timeline section: creation/modification timestamps + response window.
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function TimelineSection({ card }) {
  const r = card?.reservation || {};
  return (
    <Box>
      <SectionTitle>Chronologie</SectionTitle>
      <KvRow label="Créée le" value={formatDateTime(card?.created_at)} />
      <KvRow label="Modifiée le" value={formatDateTime(card?.modified_at)} />
      <KvRow label="Limite réponse" value={formatDateTime(card?.delayMax)} />
      <KvRow label="Jours avant événement" value={r.days_until_event != null ? `${r.days_until_event} jours` : null} />
      {card?.deleted_at && <KvRow label="Supprimé par client le" value={formatDateTime(card.deleted_at)} />}
      {card?.artist_deleted_at && <KvRow label="Supprimé par artiste le" value={formatDateTime(card.artist_deleted_at)} />}
    </Box>
  );
}

/**
 * Right-side drawer showing the full detail of a contact card.
 * Driven by the presence of the `card` prop; null means the drawer is closed.
 * @param {Object} props - card (null = closed), onClose
 * @returns {React.ReactElement}
 */
function ContactCardDetailDrawer({ card, onClose }) {
  return (
    <Drawer isOpen={Boolean(card)} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{card && <HeaderBlock card={card} />}</DrawerHeader>
        <DrawerBody>
          {card && (
            <Box>
              <ClientSection card={card} />
              <ReservationSection card={card} />
              <PaymentSection card={card} />
              <TimelineSection card={card} />
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default ContactCardDetailDrawer;
